import React, {FC, useCallback} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Modal from 'components/Modal/Modal';
import {FILTER_USERS} from 'graphql/absences/Querys';
import SearchBarController from 'controllers/SearchBarController/SearchBarController';
import {PalowanWithAssignmentCount} from 'models/PalowanVacationAssignment';

/** Shape of a user returned by FILTER_USERS query. */
interface FilterUserItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
}

/** Props for PalowanVacationCreateModal. */
export interface PalowanVacationCreateModalProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Called when the dialog is dismissed without a selection. */
  onClose: () => void;
  /** ISO country code used to scope the palowan search (e.g. 'MX'). */
  countryCode: string;
  /**
   * Called when the admin selects a palowan from the search results.
   * The parent should close this dialog and open PalowanRangesModal with the selected palowan.
   */
  onPalowanSelected: (palowan: PalowanWithAssignmentCount) => void;
}

/**
 * Palowan picker modal for initiating vacation range configuration for a palowan who
 * does not yet appear in the grouped table (no existing ranges).
 *
 * Implements the US-02.09 entry point: the admin searches for any palowan in the current
 * country and selects one; the parent then opens PalowanRangesModal so the admin can add
 * the first range using the same inline table used when editing existing palowans.
 *
 * Uses the custom Modal component (not MUI Dialog) so that the SearchBarController
 * results dropdown is not clipped by DialogContent overflow constraints.
 *
 * @param open - Controls modal visibility.
 * @param onClose - Called when the modal is dismissed without a selection.
 * @param countryCode - ISO country code scoping the FILTER_USERS search.
 * @param onPalowanSelected - Called with the selected palowan so the parent can open PalowanRangesModal.
 * @returns A Modal containing a palowan search bar.
 */
const PalowanVacationCreateModal: FC<PalowanVacationCreateModalProps> = ({
  open,
  onClose,
  countryCode,
  onPalowanSelected,
}) => {
  const {t} = useTranslation();

  /**
   * Converts the raw FILTER_USERS result to PalowanWithAssignmentCount and notifies the parent.
   * Sets totalRangeCount to 0 since this palowan has no prior assignments.
   *
   * @param item - The user item selected from the search results.
   */
  const handleResultSelect = useCallback(
    (item: FilterUserItem): void => {
      onPalowanSelected({
        palowanId: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        totalRangeCount: 0,
      });
    },
    [onPalowanSelected]
  );

  const additionalVariables = React.useMemo(
    () => ({countries: [countryCode]}),
    [countryCode]
  );

  const transformResult = useCallback(
    (item: FilterUserItem) => ({
      id: item.id,
      text: `${item.firstName} ${item.lastName}`,
    }),
    []
  );

  return (
    <Modal
      open={open}
      onClose={() => onClose()}
      title={t('leaves.PalowanVacationCreateModal.title')}
      small
    >
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          {t('leaves.PalowanVacationCreateModal.palowanSearchLabel')}
        </Typography>
        <SearchBarController<FilterUserItem>
          query={FILTER_USERS}
          queryVariableName="keyword"
          additionalVariables={additionalVariables}
          placeholder={t(
            'leaves.PalowanVacationCreateModal.palowanSearchPlaceholder'
          )}
          transformResult={transformResult}
          onResultSelect={handleResultSelect}
          size="full"
        />
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
          <Button onClick={onClose}>
            {t('leaves.PalowanVacationCreateModal.cancelButton')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PalowanVacationCreateModal;
